import React, { forwardRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';

// MUI hides the clear (X) button with opacity:0 which still occupies layout space,
// squeezing the date text. Override to use display:none instead.
const clearButtonSx = {
  '@media (pointer: fine)': {
    '& .clearButton': { display: 'none' },
    '&:hover .clearButton': { display: 'inline-flex' },
    '&:focus-within .clearButton': { display: 'inline-flex' },
  },
};

export const DateInput = forwardRef(
  (
    {
      helperText,
      error,
      onChange,
      onBlur,
      onAccept: onAcceptProp,
      value,
      name,
      ...props
    },
    ref,
  ) => {
    // Clean up props to avoid undefined values
    const cleanProps = { ...props };
    if (helperText === undefined) {
      delete cleanProps.helperText;
    }
    if (error === undefined) {
      delete cleanProps.error;
    }

    // Convert various date formats to Date object for the picker
    // Handle ISO strings, MM/dd/yyyy strings, and already parsed dates
    // Be very permissive during typing to prevent clearing
    let dateValue = null;
    if (value) {
      if (value instanceof Date) {
        dateValue = value;
      } else if (typeof value === 'string' && value.trim() !== '') {
        // Only try to parse complete dates to avoid validation during typing
        if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
          // Handle ISO format dates (from server)
          try {
            dateValue = new Date(value);
            if (isNaN(dateValue.getTime())) {
              dateValue = null;
            }
          } catch (e) {
            dateValue = null;
          }
        } else if (
          value.length === 10 &&
          value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
        ) {
          // Only try to parse MM/DD/YYYY when we have exactly 10 characters
          try {
            const [, month, day, year] = value.match(
              /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
            );
            const yearNum = parseInt(year, 10);
            const monthNum = parseInt(month, 10);
            const dayNum = parseInt(day, 10);

            // Create date without strict validation during typing
            if (
              monthNum >= 1 &&
              monthNum <= 12 &&
              dayNum >= 1 &&
              dayNum <= 31
            ) {
              const testDate = new Date(yearNum, monthNum - 1, dayNum);
              // Only validate if the date components match (handles invalid dates like Feb 31)
              if (
                testDate.getFullYear() === yearNum &&
                testDate.getMonth() === monthNum - 1 &&
                testDate.getDate() === dayNum
              ) {
                dateValue = testDate;
              }
            }
          } catch (e) {
            dateValue = null;
          }
        }
        // For incomplete dates or during typing, don't try to parse - just leave as null
      }
    }

    // Handle the value conversion for React 19 compatibility
    const handleChange = (newValue) => {
      if (onChange) {
        const isoString = newValue ? newValue.toISOString() : '';
        // Create a synthetic event-like object for compatibility
        const event = {
          target: {
            value: isoString,
            name: name,
          },
        };
        // Call with both event and ISO string for backward compatibility
        onChange(event, isoString);
      }
    };

    // Handle calendar date selection specifically
    const handleAccept = (newValue) => {
      if (onChange) {
        const isoString = newValue ? newValue.toISOString() : '';
        // Create a synthetic event-like object for compatibility
        const event = {
          target: {
            value: isoString,
            name: name,
          },
        };
        // Call with both event and ISO string for backward compatibility
        onChange(event, isoString);
      }

      // Only call the onAccept prop if we have a reasonable date
      // This prevents calling onAccept during typing when MUI creates partial dates
      if (onAcceptProp && newValue) {
        const year = newValue.getFullYear();
        // Only trigger onAccept for reasonable years (not typing artifacts like 0001, 0019, etc.)
        if (year >= 1900 && year <= new Date().getFullYear() + 10) {
          const isoString = newValue.toISOString();
          const event = {
            target: {
              value: isoString,
              name: name,
            },
          };
          onAcceptProp(event, isoString);
        }
      }
    };

    // Handle blur event - add safeguards to prevent inappropriate blur during typing
    const handleBlur = (event) => {
      // Only call onBlur if this is a real blur event, not a synthetic one during typing
      // Check if the related target is still part of the date picker component
      const isBlurringToDatePicker =
        event.relatedTarget &&
        (event.relatedTarget.closest('.MuiPickersPopper-root') ||
          event.relatedTarget.closest('.MuiDateCalendar-root') ||
          event.relatedTarget.closest('[role="dialog"]'));

      // Don't trigger blur if we're just moving focus within the date picker
      if (isBlurringToDatePicker) {
        return;
      }

      // Call the original onBlur if provided (this handles the form logic)
      if (onBlur) {
        onBlur(event);
      }
    };
    return (
      <DatePicker
        ref={ref}
        value={dateValue}
        onChange={handleChange}
        onAccept={handleAccept} // Use separate handler for calendar selection
        format='MM/dd/yyyy'
        enableAccessibleFieldDOMStructure={false}
        sx={clearButtonSx}
        slots={{
          textField: TextField,
        }}
        slotProps={{
          textField: {
            variant: 'outlined',
            size: 'small',
            color: 'secondary',
            fullWidth: true,
            error: Boolean(error),
            helperText: helperText,
            name: name,
            onBlur: handleBlur,
            inputProps: {
              'data-testid': 'date-input',
              name: name,
              placeholder: 'MM/DD/YYYY',
            },
            ...cleanProps,
          },
          field: {
            clearable: props?.clearable ? props?.clearable : true,
            onClear: () => handleChange(null),
            shouldRespectLeadingZeros: true,
          },
        }}
        onError={() => {}} // Suppress all validation errors
      />
    );
  },
);

DateInput.displayName = 'DateInput';
